import { call, put } from 'redux-saga/effects';
import _ from 'underscore';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { DraftsTelemetry } from '~src/drafts/telemetry/DraftsTelemetry';
import { extractWorkbookDraftEditUrlInfo, isUnpublishedDraft } from '~src/drafts/utils/DraftUtils';

import { requestFailed } from '../../bootstrap/redux/actions/RequestFailedAction';
import { ExtractDraftAvaliableDialog } from '../../extractTask/components/ExtractDraftAvaliableDialog';
import { EditWorkbookAction } from '../actions/EditWorkbookAction';
import { IWorkbookDetailInfo } from '../models/IWorkbookInfo';
import { fetchWorkbookById } from './WorkbookSagasHelper';

export const EditWorkbookPopupRequestTestId: string = 'edit-workbook-popup-request';

// When a new window is created, if it's not part of a click event handler it will be detected by browsers as a pop-up.
// Because we are opening a new window in the edit workbook saga which is async, to get around the pop up blocker we need
// to open the window first, then the window.location.href can be set asynchronously afterwards
export function* handleEditWorkbookAction(action: EditWorkbookAction) {
  const { workbook } = action;
  const workbook_luid = isUnpublishedDraft(workbook.draftStatus) ? workbook.draftLuid : workbook.luid;

  let editUrl: string = null;

  // Encapsulating the calls to get the workbook data
  try {
    const editUrlInfo = extractWorkbookDraftEditUrlInfo(workbook);
    if (editUrlInfo.isUnpublishedWorkbookDraft) {
      editUrl = editUrlInfo.editUrl;
    }
    else {
      const workbookDetailInfo: IWorkbookDetailInfo = yield call(fetchWorkbookById, workbook.id);
      editUrl = workbookDetailInfo?.editUrl;

      if (!_.isUndefined(workbookDetailInfo?.editExtractDraftUrl)) {
        const userExtractDraft: boolean = yield call(ExtractDraftAvaliableDialog.open);
        if (userExtractDraft) {
          editUrl = workbookDetailInfo.editExtractDraftUrl;
        }
      }
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
    // Log the error in telemetry
    if (action.draftOrigin) {
      yield call(DraftsTelemetry.sendDraftsOpenEvent, {
        success: false,
        error_message: JSON.stringify(e),
        origin: action.draftOrigin,
        workbook_luid,
      });
    }
    // Don't proceed to opening a new window if network calls fail
    return;
  }

  // Safari and Firefox block popups so we can show a specific toast asking users to allow popups.
  // Adds the url as a parameter to 'open' so if they select "allow popups" it will open the url.
  const newTab: Window = WindowLocationService.open(editUrl);
  if (newTab) {
    if (action.draftOrigin) {
      yield call(DraftsTelemetry.sendDraftsOpenEvent, {
        success: true,
        origin: action.draftOrigin,
        workbook_luid,
      });
    }
  } else {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: Messages.editWorkbook_popupRequest(),
      testId: EditWorkbookPopupRequestTestId,
    });
  }
}
